import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

export interface ApplicationScreening {
  status: 'pending' | 'completed';
  // Overall recommendation surfaced by the (FCRA-compliant) screening partner.
  recommendation: 'recommended' | 'review' | 'not_recommended' | 'pending';
  credit_score: number | null;
  // Verified monthly income divided by monthly rent (e.g. 3.1 means 3.1x rent).
  income_to_rent_ratio: number | null;
  income_verified: boolean;
  criminal: 'clear' | 'records_found' | 'pending';
  eviction: 'none' | 'records_found' | 'pending';
  provider: string;
  completed_at: string | null;
}

export interface RentalApplication {
  id: number;
  applicant_name: string;
  email: string;
  phone: string;
  property_id: number;
  property_name: string;
  unit: string;
  desired_move_in: string;
  lease_term: string;
  monthly_rent: number;
  monthly_income: number;
  employer: string;
  job_title: string;
  current_address: string;
  occupants: number;
  pets: string;
  reason_for_moving: string;
  status: 'Applied' | 'Screening' | 'Approved' | 'Declined' | 'Lease Sent' | 'Signed';
  submitted_at: string;
  application_fee_paid: boolean;
  screening: ApplicationScreening;
  // Listing channel the applicant found this unit through — one of the platforms
  // in listing-platforms.ts, surfaced as "via {source}" in the applications UI.
  source: string;
}

const DUMMY_APPLICATIONS: RentalApplication[] = [
  {
    id: 1,
    applicant_name: 'Jordan Bennett',
    email: 'jordan.bennett@email.com',
    phone: '(415) 555-0182',
    property_id: 201,
    property_name: 'Maple Street Duplex',
    unit: 'Unit A',
    desired_move_in: '2026-09-01',
    lease_term: '12 months',
    monthly_rent: 2400,
    monthly_income: 7600,
    employer: 'Northwind Labs',
    job_title: 'Software Engineer',
    current_address: '88 Bayview Ave, San Francisco, CA',
    occupants: 2,
    pets: '1 cat',
    reason_for_moving: 'Relocating closer to work',
    status: 'Screening',
    submitted_at: '2026-07-22',
    application_fee_paid: true,
    source: 'Zillow',
    screening: {
      status: 'completed',
      recommendation: 'recommended',
      credit_score: 742,
      income_to_rent_ratio: 3.2,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-23',
    },
  },
  {
    id: 2,
    applicant_name: 'Priya Raman',
    email: 'priya.raman@email.com',
    phone: '(503) 555-0147',
    property_id: 202,
    property_name: 'Oakwood Apartments',
    unit: 'Apt 3B',
    desired_move_in: '2026-08-25',
    lease_term: '12 months',
    monthly_rent: 1850,
    monthly_income: 6100,
    employer: 'Cedar Health',
    job_title: 'Registered Nurse',
    current_address: '210 Linden St, Portland, OR',
    occupants: 1,
    pets: 'None',
    reason_for_moving: 'Lease ending at current rental',
    status: 'Approved',
    submitted_at: '2026-07-20',
    application_fee_paid: true,
    source: 'Apartments.com',
    screening: {
      status: 'completed',
      recommendation: 'recommended',
      credit_score: 788,
      income_to_rent_ratio: 3.3,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-21',
    },
  },
  {
    id: 3,
    applicant_name: 'Marcus Lee',
    email: 'marcus.lee@email.com',
    phone: '(206) 555-0119',
    property_id: 201,
    property_name: 'Maple Street Duplex',
    unit: 'Unit B',
    desired_move_in: '2026-09-15',
    lease_term: 'Month-to-month',
    monthly_rent: 2400,
    monthly_income: 5400,
    employer: 'Freelance (Design)',
    job_title: 'Graphic Designer',
    current_address: '47 Pearl St, Seattle, WA',
    occupants: 1,
    pets: '1 dog (30 lbs)',
    reason_for_moving: 'Wants a larger space',
    status: 'Applied',
    submitted_at: '2026-07-24',
    application_fee_paid: false,
    source: 'Furnished Finder',
    screening: {
      status: 'pending',
      recommendation: 'pending',
      credit_score: null,
      income_to_rent_ratio: null,
      income_verified: false,
      criminal: 'pending',
      eviction: 'pending',
      provider: 'TransUnion SmartMove',
      completed_at: null,
    },
  },
  {
    id: 4,
    applicant_name: 'Danielle Foster',
    email: 'danielle.foster@email.com',
    phone: '(312) 555-0173',
    property_id: 203,
    property_name: 'Riverside Townhomes',
    unit: 'Unit 12',
    desired_move_in: '2026-09-01',
    lease_term: '12 months',
    monthly_rent: 2950,
    monthly_income: 7200,
    employer: 'Lakeside Realty',
    job_title: 'Operations Manager',
    current_address: '900 Harbor Dr, Chicago, IL',
    occupants: 3,
    pets: 'None',
    reason_for_moving: 'Family growing, need more bedrooms',
    status: 'Lease Sent',
    submitted_at: '2026-07-17',
    application_fee_paid: true,
    source: 'Zumper',
    screening: {
      status: 'completed',
      recommendation: 'review',
      credit_score: 668,
      income_to_rent_ratio: 2.4,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-19',
    },
  },
  {
    id: 5,
    applicant_name: 'Tyler Nguyen',
    email: 'tyler.nguyen@email.com',
    phone: '(669) 555-0156',
    property_id: 202,
    property_name: 'Oakwood Apartments',
    unit: 'Apt 1A',
    desired_move_in: '2026-08-20',
    lease_term: '12 months',
    monthly_rent: 1850,
    monthly_income: 3900,
    employer: 'Brightside Retail',
    job_title: 'Store Associate',
    current_address: '15 Almond Ct, San Jose, CA',
    occupants: 2,
    pets: 'None',
    reason_for_moving: 'Current building being sold',
    status: 'Declined',
    submitted_at: '2026-07-15',
    application_fee_paid: true,
    source: 'Zillow',
    screening: {
      status: 'completed',
      recommendation: 'not_recommended',
      credit_score: 588,
      income_to_rent_ratio: 2.1,
      income_verified: true,
      criminal: 'clear',
      eviction: 'records_found',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-16',
    },
  },
  {
    id: 6,
    applicant_name: 'Sofia Martinez',
    email: 'sofia.martinez@email.com',
    phone: '(702) 555-0190',
    property_id: 203,
    property_name: 'Riverside Townhomes',
    unit: 'Unit 4',
    desired_move_in: '2026-08-15',
    lease_term: '12 months',
    monthly_rent: 2950,
    monthly_income: 9800,
    employer: 'Summit Financial',
    job_title: 'Account Director',
    current_address: '320 Vista Blvd, Las Vegas, NV',
    occupants: 2,
    pets: '1 cat',
    reason_for_moving: 'Job transfer',
    status: 'Signed',
    submitted_at: '2026-07-13',
    application_fee_paid: true,
    source: 'Apartments.com',
    screening: {
      status: 'completed',
      recommendation: 'recommended',
      credit_score: 801,
      income_to_rent_ratio: 3.3,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-14',
    },
  },
  // --- Room-splitting demo: waitlist applicants for Palmetto House, Room D (unit 9) ---
  {
    id: 7,
    applicant_name: 'Brianna Coles',
    email: 'brianna.coles@email.com',
    phone: '(813) 555-0299',
    property_id: 9,
    property_name: 'Palmetto House',
    unit: 'Room D',
    desired_move_in: '2026-09-08',
    lease_term: 'Month-to-month',
    monthly_rent: 750,
    monthly_income: 3400,
    employer: 'MUSC Health',
    job_title: 'Medical Assistant',
    current_address: '412 Meeting St, Charleston, SC',
    occupants: 1,
    pets: 'None',
    reason_for_moving: 'Shorter commute to work',
    status: 'Screening',
    submitted_at: '2026-07-30',
    application_fee_paid: true,
    source: 'PadSplit',
    screening: {
      status: 'completed',
      recommendation: 'recommended',
      credit_score: 695,
      income_to_rent_ratio: 4.5,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-31',
    },
  },
  {
    id: 8,
    applicant_name: 'Tomás Reyes',
    email: 'tomas.reyes@email.com',
    phone: '(813) 555-0288',
    property_id: 9,
    property_name: 'Palmetto House',
    unit: 'Room D',
    desired_move_in: '2026-10-01',
    lease_term: 'Month-to-month',
    monthly_rent: 750,
    monthly_income: 2900,
    employer: 'Rooter Plumbing Co.',
    job_title: 'Apprentice Plumber',
    current_address: '88 Rutledge Ave, Charleston, SC',
    occupants: 1,
    pets: 'None',
    reason_for_moving: 'Moving out of a shared lease',
    status: 'Applied',
    submitted_at: '2026-08-01',
    application_fee_paid: true,
    source: 'PadSplit',
    screening: {
      status: 'pending',
      recommendation: 'pending',
      credit_score: null,
      income_to_rent_ratio: null,
      income_verified: false,
      criminal: 'pending',
      eviction: 'pending',
      provider: 'TransUnion SmartMove',
      completed_at: null,
    },
  },
  // --- Room-splitting demo: applicant for Ferncreek Rooming House, Room 3 (unit 10) ---
  {
    id: 9,
    applicant_name: 'Sasha Alvarado',
    email: 'sasha.alvarado@email.com',
    phone: '(614) 555-0277',
    property_id: 10,
    property_name: 'Ferncreek Rooming House',
    unit: 'Room 3',
    desired_move_in: '2026-09-10',
    lease_term: 'Month-to-month',
    monthly_rent: 650,
    monthly_income: 2600,
    employer: 'Columbus Public Library',
    job_title: 'Library Assistant',
    current_address: '210 High St, Columbus, OH',
    occupants: 1,
    pets: 'None',
    reason_for_moving: 'First apartment after college',
    status: 'Screening',
    submitted_at: '2026-07-27',
    application_fee_paid: true,
    source: 'PadSplit',
    screening: {
      status: 'completed',
      recommendation: 'review',
      credit_score: 632,
      income_to_rent_ratio: 4.0,
      income_verified: true,
      criminal: 'clear',
      eviction: 'none',
      provider: 'TransUnion SmartMove',
      completed_at: '2026-07-28',
    },
  },
];

@Injectable({
  providedIn: 'root'
})
export class RentalApplicationsService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // Applications have no backend yet — the sample pipeline belongs only to the
  // demo account; real accounts must see an empty list, never fabricated data.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async getApplications(): Promise<RentalApplication[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_APPLICATIONS : [];
    }
    return firstValueFrom(
      this.http.get<RentalApplication[]>(`${environment.api_url}api/v1/customer/rental-applications`)
    );
  }

  async getApplication(id: number): Promise<RentalApplication | undefined> {
    if (this.useDummyData) {
      return DUMMY_APPLICATIONS.find(a => a.id === id);
    }
    return firstValueFrom(
      this.http.get<RentalApplication>(`${environment.api_url}api/v1/customer/rental-applications/${id}`)
    );
  }

  async updateApplicationStatus(id: number, status: RentalApplication['status']): Promise<RentalApplication> {
    if (this.useDummyData) {
      const idx = DUMMY_APPLICATIONS.findIndex(a => a.id === id);
      if (idx >= 0) {
        DUMMY_APPLICATIONS[idx] = { ...DUMMY_APPLICATIONS[idx], status };
        return DUMMY_APPLICATIONS[idx];
      }
      throw new Error('Application not found');
    }
    return firstValueFrom(
      this.http.put<RentalApplication>(`${environment.api_url}api/v1/customer/rental-applications/${id}`, { status })
    );
  }
}
